import { BookingSource } from '@vendasta/meetings';
import { v4 as uuidv4 } from 'uuid';

const BOOKING_REQUEST_ID_COOKIE_PREFIX = 'bookme_request_id__';
const BOOKING_REQUEST_ID_TTL_MINUTES = 30;

export function getOrCreateUniqueRequestId(
  entityId: string | undefined
): string {
  if (!entityId || typeof document === 'undefined') {
    return uuidv4();
  }
  const cookieName = `${BOOKING_REQUEST_ID_COOKIE_PREFIX}${entityId}`;
  const existing = readBookingRequestCookie(cookieName);
  if (existing) {
    return existing;
  }
  const newId = uuidv4();
  writeBookingRequestCookie(cookieName, newId, BOOKING_REQUEST_ID_TTL_MINUTES);
  return newId;
}

// Deletes every booking-intent cookie sharing a UUID with one of the named
// entity keys — handles the slug+canonical pair migrate writes without
// touching cookies belonging to other entities in this browser.
export function clearUniqueRequestId(
  ...entityIds: (string | undefined)[]
): void {
  if (typeof document === 'undefined') {
    return;
  }

  const uuidsToClear = new Set<string>();
  const seen = new Set<string>();
  for (const entityId of entityIds) {
    if (!entityId || seen.has(entityId)) {
      continue;
    }
    seen.add(entityId);
    const value = readBookingRequestCookie(
      `${BOOKING_REQUEST_ID_COOKIE_PREFIX}${entityId}`
    );
    if (value) {
      uuidsToClear.add(value);
    }
  }

  for (const entry of document.cookie.split('; ')) {
    const equalsIdx = entry.indexOf('=');
    if (equalsIdx <= 0) {
      continue;
    }
    let name: string;
    let value: string;
    try {
      name = decodeURIComponent(entry.slice(0, equalsIdx));
      value = decodeURIComponent(entry.slice(equalsIdx + 1));
    } catch {
      continue;
    }
    if (
      name.startsWith(BOOKING_REQUEST_ID_COOKIE_PREFIX) &&
      uuidsToClear.has(value)
    ) {
      deleteBookingRequestCookie(name);
    }
  }
}

// Copies the UUID from the slug key to the canonical key after a GET
// response resolves slug → canonical. Both keys are kept (TTL refreshed) so
// a page refresh — which fires the GET again with the slug — still finds
// the UUID and reuses it instead of orphaning the PENDING row backend-side.
export function migrateUniqueRequestId(
  fromEntityId: string | undefined,
  toEntityId: string | undefined
): void {
  if (
    !fromEntityId ||
    !toEntityId ||
    fromEntityId === toEntityId ||
    typeof document === 'undefined'
  ) {
    return;
  }
  const oldCookieName = `${BOOKING_REQUEST_ID_COOKIE_PREFIX}${fromEntityId}`;
  const existing = readBookingRequestCookie(oldCookieName);
  if (!existing) {
    return;
  }
  writeBookingRequestCookie(
    `${BOOKING_REQUEST_ID_COOKIE_PREFIX}${toEntityId}`,
    existing,
    BOOKING_REQUEST_ID_TTL_MINUTES
  );
  writeBookingRequestCookie(
    oldCookieName,
    existing,
    BOOKING_REQUEST_ID_TTL_MINUTES
  );
}

// Returns BOOKING_SOURCE_CRM when ?bookingSource=CRM, else BOOKING_FORM.
// Called by every GET and BOOK so backend (attemptID, source, entityID)
// tuples match.
export function resolveBookingSource(): BookingSource {
  if (typeof window === 'undefined' || !window.location) {
    return BookingSource.BOOKING_SOURCE_BOOKING_FORM;
  }
  const param = new URLSearchParams(window.location.search).get('bookingSource');
  return param?.toUpperCase() === 'CRM'
    ? BookingSource.BOOKING_SOURCE_CRM
    : BookingSource.BOOKING_SOURCE_BOOKING_FORM;
}

function readBookingRequestCookie(name: string): string | undefined {
  const prefix = `${encodeURIComponent(name)}=`;
  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : undefined;
}

function writeBookingRequestCookie(
  name: string,
  value: string,
  ttlMinutes: number
): void {
  const expires = new Date(Date.now() + ttlMinutes * 60_000).toUTCString();
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${cookieSecureAttribute()}`;
}

function deleteBookingRequestCookie(name: string): void {
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax${cookieSecureAttribute()}`;
}

// Adding Secure on http:// makes the browser drop the cookie, so it's
// conditional on HTTPS to keep dev/preview environments working.
function cookieSecureAttribute(): string {
  if (typeof location === 'undefined') {
    return '';
  }
  return location.protocol === 'https:' ? '; Secure' : '';
}
